import * as THREE from 'three';
import { cosmeticById, type ModKind } from '@/config/cosmetics';
import { lookColors, type CarLook } from '@/entities/carLook';
import { modAnchorsFor } from './modAnchors';
import { ModMaterials, disposeGeometries } from './modKit';
import type { SurfaceField } from './modSurface';
import type { ModCtx, ModPart, PartFn } from './modTypes';
import * as hard from './partsHard';
import * as soft from './partsSoft';
import { flags as flagsPart } from './partsFlags';

/**
 * ModRig.ts (M128) — everything a car wears from the MODS tab, as one group
 * to hang off the car's body (CarView `body`, a garage bay's car holder, a
 * lobby seat, the podium car). Builds only the mods whose catalog `fits`
 * lists this ride, from the ride's measured anchors, in the kit's colours.
 *
 * PRESENTATION ONLY (owner rule, phase 1): `update` reads the render snapshot
 * (speed, lateral speed, acceleration, boost, possession) — never the sim —
 * so mods can neither desync an online race nor change a lap time.
 */

const BUILDERS: Record<ModKind, PartFn> = {
  gloves: hard.gloves,
  floods: hard.floods,
  kitnumber: hard.kitnumber,
  cleats: hard.cleats,
  whistle: hard.whistle,
  rally: hard.rally,
  trophy: hard.trophy,
  flags: flagsPart,
  goalwing: soft.goalwing,
  mudflaps: hard.mudflaps,
  ponytail: soft.ponytail,
  tifo: soft.tifo,
  fans: soft.fans,
};

/** M153: every part now stands on the MEASURED body (the cape's drape, the
 *  hair's floor, the rally pod's feet, a glove's cuff, a flood's base, a
 *  whistle at the real tail, a sill plate at the real rocker), so the whole
 *  rig is rebuilt once when the surface lands (M128c rebuilt three kinds). */

let reducedMotion = false;
/** The game's Reduced Motion setting (and Low quality) park every mod
 *  motion — the shapes stay, nothing sways. */
export function setModsReducedMotion(on: boolean): void {
  reducedMotion = on;
}

interface BuiltPart {
  kind: ModKind;
  part: ModPart;
}

export class ModRig {
  readonly group = new THREE.Group();
  private readonly built: BuiltPart[] = [];
  private readonly mats = new ModMaterials();
  private t = 0;
  private settled = false;
  private readonly ctx: ModCtx = { t: 0, speed: 0, lateralSpeed: 0, accel: 0, boosting: false, dribbling: false };
  private readonly build: ((kind: ModKind) => ModPart) | null = null;
  private surface: SurfaceField | null;

  constructor(
    ids: readonly string[],
    kitNumber: number,
    vehicleId: string,
    stockColor: number,
    look: CarLook | undefined,
    surface: SurfaceField | null = null,
    tifoStyle = 1,
  ) {
    this.surface = surface;
    const a = modAnchorsFor(vehicleId);
    if (!a) return;
    const c = lookColors(look, stockColor);
    this.build = (kind): ModPart => BUILDERS[kind](a, c, this.mats, { kitNumber, mods: ids, tifoStyle, surface: this.surface });
    for (const id of ids) {
      const item = cosmeticById(id);
      if (!item?.mod || !item.mod.fits.includes(vehicleId)) continue;
      const part = this.build(item.mod.kind);
      this.built.push({ kind: item.mod.kind, part });
      this.group.add(part.group);
    }
  }

  get empty(): boolean {
    return this.built.length === 0;
  }

  /** The ride's measured surface just landed (or changed): rebuild the parts
   *  that rest on it. Materials stay tracked by the rig, geometry is freed. */
  setSurface(surface: SurfaceField | null): void {
    this.surface = surface;
    if (!this.build) return;
    for (const b of this.built) {
      b.part.dispose?.();
      disposeGeometries(b.part.group);
      b.part.group.removeFromParent();
      b.part = this.build(b.kind);
      this.group.add(b.part.group);
    }
    this.settled = false;
  }

  update(speed: number, lateralSpeed: number, accel: number, boosting: boolean, dribbling: boolean, dt: number): void {
    if (reducedMotion) {
      // (once, at rest and with no time passing: the static state parts read
      // from each other — the tail number under a resting cape — resolves,
      // and nothing moves)
      if (!this.settled) {
        this.settled = true;
        this.step(0, 0, 0, false, false, 0);
      }
      return;
    }
    this.step(speed, lateralSpeed, accel, boosting, dribbling, dt);
  }

  private step(speed: number, lateralSpeed: number, accel: number, boosting: boolean, dribbling: boolean, dt: number): void {
    this.t += dt;
    const c = this.ctx;
    c.t = this.t;
    c.speed = speed;
    c.lateralSpeed = lateralSpeed;
    c.accel = accel;
    c.boosting = boosting;
    c.dribbling = dribbling;
    // (the cape first: it publishes where its cloth is for the parts that
    // yield to it, the ponytail's floor and the tail number's cover)
    for (const b of this.built) if (b.kind === 'tifo') b.part.update?.(c, dt);
    for (const b of this.built) if (b.kind !== 'tifo') b.part.update?.(c, dt);
  }

  dispose(): void {
    for (const b of this.built) b.part.dispose?.();
    disposeGeometries(this.group);
    this.mats.dispose();
    this.group.removeFromParent();
  }
}

/** Build a rig for a look, or null when it wears no mods this ride can take.
 *  `surface` = the ride's measured top when its model is already in. */
export function buildModRig(
  look: CarLook | undefined,
  vehicleId: string,
  stockColor: number,
  surface: SurfaceField | null = null,
): ModRig | null {
  const mods = look?.mods;
  if (!mods || mods.ids.length === 0) return null;
  const rig = new ModRig(mods.ids, mods.kitNumber, vehicleId, stockColor, look, surface, mods.tifoStyle);
  if (rig.empty) {
    rig.dispose();
    return null;
  }
  return rig;
}

/** Build + hang a rig on a car-local parent (garage bay, lobby seat, podium). */
export function attachModRig(
  parent: THREE.Object3D,
  look: CarLook | undefined,
  vehicleId: string,
  stockColor: number,
  surface: SurfaceField | null = null,
): ModRig | null {
  const rig = buildModRig(look, vehicleId, stockColor, surface);
  if (rig) parent.add(rig.group);
  return rig;
}
